import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api } from '../../shared/utils/api';
import { confColor, setThresholds } from './confColorUtils';
import SubjectGroup from './TopicGroup';
import { DEFAULT_STATUSES } from './TopicDetail';

export default function TopicsPage({ module, focusTopicId, onFocusConsumed, tourHighlight, onNavigateToSRA }) {
  const [topics, setTopics] = useState([]);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [groupsOpen, setGroupsOpen] = useState(null);
  const [statuses, setStatuses] = useState(DEFAULT_STATUSES);
  const [sraTopicStats, setSraTopicStats] = useState({});
  const [thRed, setThRed] = useState(0.4);
  const [thGreen, setThGreen] = useState(0.7);

  const [searchParams] = useSearchParams();
  const urlFocusId = parseInt(searchParams.get('focus'), 10) || null;
  const effectiveFocusId = focusTopicId || urlFocusId;

  useEffect(() => {
    api('/api/settings').then(s => {
      try {
        const custom = JSON.parse(s.custom_statuses || '[]');
        setStatuses(custom.length > 0 ? custom : DEFAULT_STATUSES);
      } catch { setStatuses(DEFAULT_STATUSES); }
      let newRed = thRed, newGreen = thGreen;
      if (s.confidence_threshold_red) { newRed = parseFloat(s.confidence_threshold_red); setThRed(newRed); }
      if (s.confidence_threshold_green) { newGreen = parseFloat(s.confidence_threshold_green); setThGreen(newGreen); }
      setThresholds(newRed, newGreen);
    }).catch((err) => { console.error('[Topics] Failed to load settings:', err); });
  }, []);

  useEffect(() => {
    api('/api/sra/topic-stats').then(data => {
      if (data && typeof data === 'object' && !data.msg) setSraTopicStats(data);
    }).catch((err) => { console.error('[Topics] Failed to load SRA topic stats:', err); });
  }, []);

  const moduleVariant = module === 'FLK1' ? 'flk1' : 'flk2';
  const moduleColor = module === 'FLK1' ? 'var(--flk1)' : 'var(--flk2)';

  useEffect(()=>{
    setLoading(true); setTopics([]); setFilter('all'); setSearch('');
    api(`/api/topics/${module}`).then(data=>{setTopics(data);setLoading(false);});
  },[module]);

  useEffect(()=>{
    if(effectiveFocusId&&!loading){
      const timer=setTimeout(()=>onFocusConsumed&&onFocusConsumed(),1500);
      return()=>clearTimeout(timer);
    }
  },[effectiveFocusId,loading,onFocusConsumed]);

  const handleSave=useCallback((id,updates)=>{
    setTopics(prev=>prev.map(t=>t.id===id?{...t,...updates}:t));
  },[]);

  const handleRateAll = useCallback(async (generalTopic, childTopics, confidence) => {
    // Optimistic update
    setTopics(prev => prev.map(t =>
      t.general_topic === generalTopic
        ? { ...t, confidence, is_estimated: true }
        : t
    ));
    await api('/api/topics/cascade-by-subject', {
      method: 'POST',
      body: JSON.stringify({ level: 'L2', general_topic: generalTopic, module, confidence }),
    });
  }, [module]);

  // Search filter (L3 level — determines which L3 topics are visible)
  const searchFiltered = useMemo(() => {
    const q = search.toLowerCase();
    if (!q) return topics;
    return topics.filter(t =>
      (t.subtopic || '').toLowerCase().includes(q) ||
      (t.display_name || '').toLowerCase().includes(q) ||
      (t.main_topic || t.topic || '').toLowerCase().includes(q) ||
      (t.general_topic || '').toLowerCase().includes(q) ||
      (t.sub_subtopic || t.detail || '').toLowerCase().includes(q)
    );
  }, [topics, search]);

  // Compute L2 groups from full unfiltered topics (for filter pill counts)
  const allL2Groups = useMemo(() => {
    const groups = {};
    topics.forEach(t => {
      const key = t.general_topic || 'General';
      if (!groups[key]) groups[key] = [];
      groups[key].push(t);
    });
    return Object.entries(groups).map(([name, children]) => {
      const hasAny = children.some(t => t.confidence !== null);
      const avg = hasAny
        ? children.reduce((s, t) => s + (t.confidence || 0), 0) / children.length
        : null;
      return { name, children, avg, hasAny };
    });
  }, [topics]);

  const counts = {
    all: topics.length,
    weak: topics.filter(t => t.confidence !== null && t.confidence < thRed).length,
    untouched: topics.filter(t => t.confidence === null).length,
    strong: topics.filter(t => t.confidence !== null && t.confidence >= thGreen).length,
  };

  // Apply filter pill at L2 level — determine which general topics pass
  const passingL2Names = useMemo(() => {
    if (filter === 'all') return null; // null = show all
    const l2Groups = {};
    searchFiltered.forEach(t => {
      const key = t.general_topic || 'General';
      if (!l2Groups[key]) l2Groups[key] = [];
      l2Groups[key].push(t);
    });
    const passing = new Set();
    Object.entries(l2Groups).forEach(([name, children]) => {
      const hasAny = children.some(t => t.confidence !== null);
      const avg = hasAny
        ? children.reduce((s, t) => s + (t.confidence || 0), 0) / children.length
        : null;
      if (filter === 'weak' && avg !== null && avg < thRed) passing.add(name);
      if (filter === 'untouched' && !hasAny) passing.add(name);
      if (filter === 'strong' && avg !== null && avg >= thGreen) passing.add(name);
    });
    return passing;
  }, [searchFiltered, filter]);

  // Final filtered topics: search-matched + L2-filter-passing
  const filtered = useMemo(() => {
    if (passingL2Names === null) return searchFiltered; // 'all' filter
    return searchFiltered.filter(t => {
      const l2 = t.general_topic || 'General';
      return passingL2Names.has(l2);
    });
  }, [searchFiltered, passingL2Names]);

  // Group by main_topic (uses new column if present, falls back to topic field)
  const grouped = {};
  const subjectOrder = [];
  filtered.forEach(t => {
    const key = t.main_topic || t.topic || 'Other';
    if (!grouped[key]) { grouped[key] = []; subjectOrder.push(key); }
    grouped[key].push(t);
  });
  const uniqueSubjects = [...new Set(subjectOrder)];

  const flk1Desc = 'Business Law · Dispute Resolution · Contract Law · Tort · Legal System · Constitutional & Administrative Law · Legal Services · Ethics';
  const flk2Desc = 'Property Law & Practice · Wills & Administration · Solicitors Accounts · Land Law · Trusts Law · Criminal Liability · Criminal Practice · Ethics';

  if(loading) return <div className="loading">Loading {module}</div>;

  return (
    <div className="page-container">
    <div className="topics-page">
      <h1 className="page-heading">{module} topics</h1>
      <div className={`module-header-bar module-header-bar-${moduleVariant}`}>
        <span className={`module-tag module-tag-${moduleVariant}`}>{module}</span>
        <span className="module-desc">
          {module==='FLK1' ? flk1Desc : flk2Desc}
        </span>
      </div>

      <div className="topics-toolbar">
        <input className="search-input" placeholder="Search topics, areas, keywords" value={search} onChange={e=>setSearch(e.target.value)}/>
        <button className="collapse-btn" onClick={()=>setGroupsOpen(true)}>Expand all</button>
        <button className="collapse-btn" onClick={()=>setGroupsOpen(false)}>Collapse all</button>
      </div>

      <div className="filter-row">
        {[
          ['all','All'],
          ['weak','Needs work'],
          ['untouched','Not started'],
          ['strong','Strong'],
        ].map(([k,label])=>(
          <button key={k} className={`filter-btn filter-btn-${k} ${filter===k?'active':''}`}
            onClick={()=>setFilter(k)}>
            {label} <span className="filter-count">{counts[k]}</span>
          </button>
        ))}
      </div>

      {filtered.length===0&&<div className="empty">No topics match this filter</div>}

      {uniqueSubjects.map((subject, i) => (
        <SubjectGroup
          key={subject}
          subject={subject}
          topics={grouped[subject]}
          onSave={handleSave}
          forceOpen={groupsOpen}
          moduleColor={moduleColor}
          focusTopicId={effectiveFocusId}
          statuses={statuses}
          highlightFirstTopic={tourHighlight && i === 0}
          sraStat={sraTopicStats[subject] || null}
          sraTopicStats={sraTopicStats}
          onRateAll={handleRateAll}
          autoExpandL2={!!search}
          onNavigateToSRA={onNavigateToSRA}
        />
      ))}
    </div>
    </div>
  );
}
